/**
 * Playtest detail — INVENTORY-DESKTOP §4 row 2 (lane D4). Prototype lines
 * 798-873. Mounted by `DesktopApp` while `s.ptDet`; closes itself.
 *
 * `selectors.3e#ptDetailVM` carries the whole bag; `selectors.d4#ptDetailVMDesktop`
 * wraps it for the one reworded clause (§8: `…ANYONE CAN UPDATE, EVERY EDIT IS
 * ATTRIBUTED.`). What this file owns, per the no-closures rule in
 * `selectors.ts`: the store actions behind `clrKind` / `actKind` / `fpUrl`.
 *
 * DESKTOP LAYOUT vs the mobile sheet
 *   hero        200px (mobile 150px), name pixel 13px
 *   banner      ONE row — dot · state · sub · spacer · the party squares
 *               RIGHT-ALIGNED over their label (mobile stacked two rows)
 *   boxes       `grid-template-columns:repeat(3,1fr); gap:12px` (mobile stacked)
 *   people ✕    a bare `✕` box with a tooltip (mobile spelled `✕ CLEAR`)
 *   actions     one `gap:10px` row: INSTALL/REQUEST `flex:1 min-180`,
 *               MARK INTEREST `flex:1 min-160`, UNTRACK (mobile stacked the
 *               primary above the pair)
 *
 * `style-hover` → CSS (§3.11): `d-hov-acc2-box` on the person ✕,
 * `d-hov-acc-box` on the box action.
 *
 * UNTRACK stays two-tap on a crew-wide playtest (`unTxt` → `SURE?`) and one tap
 * on an `auto` one, which is a per-user mute — the app behaviour the prototype's
 * bare `UNTRACK` predates (docs/porting/P4b.md §7, PORTING-NOTES D-04).
 */

import { Avatar } from '../../components/Avatar.tsx';
import { PixelTriangle } from '../../components/PixelTriangle.tsx';
import { PtHeroArt } from '../../components/PtArt.tsx';
import { useQd } from '../../store/index.ts';
import { ptDetailVMDesktop, PT_CLEAR_TIP } from '../../store/selectors.d4.ts';
import { Modal } from '../components/Modal.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/** Header notes are mono 10.5px `--gt-muted` in the prototype (§4). */
const NOTE: CSSProperties = { fontSize: '10.5px', color: 'var(--gt-muted)' };

/** The ↗ glyph on the primary CTA (prototype line 867). */
function Arrow() {
  return (
    <svg width="9" height="9" viewBox="0 0 9 9" style={{ display: 'block', flex: 'none' }} aria-hidden="true">
      <rect x="3" y="0" width="6" height="2" fill="currentColor" />
      <rect x="7" y="0" width="2" height="6" fill="currentColor" />
      <rect x="5" y="2" width="2" height="2" fill="currentColor" />
      <rect x="3" y="4" width="2" height="2" fill="currentColor" />
      <rect x="1" y="6" width="2" height="2" fill="currentColor" />
    </svg>
  );
}

export default function PtDetailModal(): JSX.Element | null {
  const s = useQd();
  const v = ptDetailVMDesktop(s, Date.now());
  if (!v) return null;

  return (
    <Modal
      label="Playtest detail modal"
      width={700}
      onClose={s.closePtDet}
      header={{
        title: v.headKind,
        titleClr: 'var(--acc2)',
        note: <span style={NOTE}>{v.headNote}</span>,
        sticky: true,
      }}
    >
      {/* 200px hero */}
      <div style={{ position: 'relative', height: 200, flex: 'none', background: v.heroBg }}>
        <div style={{ position: 'absolute', inset: 0, background: 'var(--gt-checker)' }} />
        <PtHeroArt id={v.id} cands={v.cands} ini={v.ini} fs={22} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,transparent 30%,rgba(5,5,8,.92))' }} />
        <div style={{ position: 'absolute', left: 18, bottom: 14, right: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontFamily: PIXEL, fontSize: 13, color: 'var(--gt-text)', lineHeight: 1.5 }}>{v.name}</div>
          <div style={{ fontFamily: MONO, fontSize: '10.5px', color: 'var(--gt-text-3)' }}>{v.sub}</div>
        </div>
        {v.isOff ? (
          <div
            style={{
              position: 'absolute',
              right: 14,
              top: 12,
              fontFamily: PIXEL,
              fontSize: 7,
              background: 'var(--acc2)',
              color: 'var(--gt-ink)',
              padding: '5px 6px',
            }}
          >
            {v.srcChip}
          </div>
        ) : null}
      </div>

      {/* status banner — squares right-aligned */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          margin: '16px 18px 0',
          border: '1px solid ' + v.bnBd,
          background: v.bnBg,
          padding: '12px 14px',
          flexWrap: 'wrap',
        }}
      >
        {v.bnLive ? (
          <span
            style={{ width: 8, height: 8, background: 'var(--acc)', animation: 'gt-blink 1.1s steps(1) infinite', flex: 'none' }}
          />
        ) : null}
        <div style={{ fontFamily: PIXEL, fontSize: '8.5px', color: v.bnClr, lineHeight: 1.6, transform: 'translateY(0.13em)' }}>
          {v.bnTxt}
        </div>
        <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-text-5)' }}>{v.bnSub}</div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {v.sqs.map((q) => (
              <div key={q.key} style={{ width: 12, height: 12, background: q.bg, border: '1px solid ' + q.bd, marginLeft: q.ml }} />
            ))}
          </div>
          <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.05em', color: 'var(--gt-text-5)' }}>
            <b style={{ color: 'var(--acc)' }}>{v.sqLbl}</b>
          </div>
        </div>
      </div>

      {/* off-Steam provenance */}
      {v.isOff ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            margin: '12px 18px 0',
            border: '1px dashed var(--acc2)',
            background: 'rgba(240,90,190,.04)',
            padding: '11px 13px',
          }}
        >
          <Avatar style={{ flex: 'none' }} size={20} fs={7} ini={v.byIni} bg={v.byBg} hasAv={v.byHasAv} avBg={v.byAvBg} />
          <div style={{ fontFamily: MONO, fontSize: '9.5px', lineHeight: 1.7, color: 'var(--gt-text-4)' }}>{v.attrTxt}</div>
        </div>
      ) : null}

      {/* the three people boxes, side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, margin: '14px 18px 0' }}>
        {v.boxes.map((c) => (
          <div
            key={c.key}
            style={{
              border: '1px solid var(--gt-line)',
              background: 'var(--gt-panel)',
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 9,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontFamily: MONO,
                fontSize: '8.5px',
                letterSpacing: '.08em',
                color: c.hc,
              }}
            >
              {c.tri ? <PixelTriangle w={7} h={6} /> : null}
              {c.h}
            </div>

            {c.rows.map((p) => (
              <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar style={{ flex: 'none' }} size={18} fs={6} ini={p.i} bg={p.bg} hasAv={p.hasAv} avBg={p.avBg} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--gt-text)' }}>{p.name}</div>
                  <div style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.03em', color: 'var(--gt-muted)' }}>{p.meta}</div>
                </div>
                {p.hasClr ? (
                  <div
                    onClick={() => {
                      if (p.clrKind === 'int') void s.ptToggleInt(v.id);
                      else void s.ptClearSt(v.id);
                    }}
                    className="d-hov-acc2-box"
                    data-tip={PT_CLEAR_TIP}
                    data-tip-pos="r"
                    style={{
                      marginLeft: 'auto',
                      border: '1px solid var(--gt-border-3)',
                      color: 'var(--gt-text-5)',
                      fontFamily: MONO,
                      fontSize: '8.5px',
                      padding: '3px 6px',
                      cursor: 'pointer',
                      flex: 'none',
                    }}
                  >
                    ✕
                  </div>
                ) : null}
              </div>
            ))}

            {c.none ? <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-ghost)' }}>—</div> : null}

            {c.hasAct ? (
              <div
                onClick={() => {
                  if (c.actKind) void s.ptReport(v.id, c.actKind);
                }}
                className="d-hov-acc-box"
                style={{
                  marginTop: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed var(--gt-border-3)',
                  color: 'var(--gt-text-3)',
                  fontFamily: MONO,
                  fontSize: '9.5px',
                  letterSpacing: '.03em',
                  padding: '7px 8px',
                  cursor: 'pointer',
                }}
              >
                {c.act}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {/* Both kinds now — see the twin comment in `sheets/PtDetailSheet.tsx`. */}
      <div style={{ margin: '12px 18px 0', fontFamily: MONO, fontSize: 9, lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
        {v.selfNote}
      </div>

      <div style={{ margin: '14px 18px 0', fontSize: 12, lineHeight: 1.6, color: 'var(--gt-text-4)' }}>{v.desc}</div>

      {/* official link + the two scam / provenance notes */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          margin: '16px 18px 0',
          border: '1px solid var(--gt-line)',
          background: 'var(--gt-panel)',
          padding: '11px 13px',
          flexWrap: 'wrap',
        }}
      >
        {v.hasLink ? (
          <a
            href={v.linkHref}
            target="_blank"
            rel="noreferrer"
            style={{
              border: '1px solid var(--acc)',
              color: 'var(--acc)',
              fontFamily: MONO,
              fontSize: 10,
              padding: '7px 11px',
              textDecoration: 'none',
            }}
          >
            {v.linkTxt}
          </a>
        ) : null}
        <div style={{ fontFamily: MONO, fontSize: 9, lineHeight: 1.6, color: 'var(--gt-muted-2)' }}>
          {v.note1}
          <br />
          {v.note2}
        </div>
      </div>

      {/* actions */}
      <div style={{ display: 'flex', gap: 10, padding: '16px 18px 18px', flexWrap: 'wrap' }}>
        {v.hasFp ? (
          <div
            onClick={() => {
              window.open(v.fpUrl, '_blank');
              // Only the SIGNUP hand-off asks; INSTALL is somebody already in.
              if (v.fpAsk) s.openPtAsk(v.id);
            }}
            style={{
              flex: 1,
              minWidth: 180,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'var(--acc)',
              color: 'var(--gt-ink)',
              fontFamily: PIXEL,
              fontSize: '8.5px',
              padding: '15px 10px',
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            {v.fpTxt}
            <Arrow />
          </div>
        ) : null}
        <div
          onClick={() => void s.ptToggleInt(v.id)}
          style={{
            flex: 1,
            minWidth: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            border: '2px solid ' + v.fiBd,
            color: v.fiClr,
            background: v.fiBg,
            fontFamily: PIXEL,
            fontSize: '8.5px',
            padding: '13px 10px',
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          <PixelTriangle w={9} h={8} />
          {v.fiTxt}
        </div>
        <div
          onClick={() => {
            if (v.auto || s.ptRmArm === v.id) void s.ptUntrack(v.id);
            else s.setPtRmArm(v.id);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid ' + v.unBd,
            color: v.unClr,
            fontFamily: MONO,
            fontSize: '10.5px',
            padding: '12px 14px',
            cursor: 'pointer',
          }}
        >
          {v.unTxt}
        </div>
      </div>
    </Modal>
  );
}
